import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { mobile, tablet } from "../../../design-system/tokens/breakpoints";
import { useDispatch, useSelector } from "react-redux";
import {
    PathwayCenteredTableCellContent,
    PathwayTable,
    PathwayTableCellContent,
    PathwayTableContentRow,
    PathwayTableHeader,
    PathwayTableHeaderRow,
    TableContentDividerLine,
} from "../../../_UI/StyledComponents/PathwayTable";
import { PathwayButton } from "../../../_UI/Components/PathwayButtons";
import stringUtils from "../../../_services/string-utils";
import PathwayConnectionChip from "../../../_UI/Components/PathwayConnectionChip";
import PathwayStatusPill from "../../../_UI/Components/PathwayStatusPill";
import { getIntegrationIcon, getIntegrationLogo } from "../../../_statics/IntegrationBrand";
import { connectionStatusSeverity, isAwaitingSetup, isStalledPaypalSetup } from "./connectionStatus";
import PathwayRowActions from "../../../_UI/Components/PathwayRowActions";
import PathwayHint from "../../../_UI/Components/PathwayHint";
import { PathwayIconButton } from "../../../_UI/StyledComponents/PathwayIcons";
import { WarningMessage } from "../../../_UI/Components/Alerts/PathwayAlertMessages";
import { getDatevConnections } from "../services/clients.actions";
import LoadingSpinner from "../../../_UI/Components/LoadingSpinner";
import { AuthDataStatus } from "../../../_statics/AuthDataStatus";
import { UserRole } from "../../../_statics/UserStatics";

const ClientsScreenClientsTabContent = (props) => {
    const dispatch = useDispatch();
    const clientReducer = useSelector((state) => state.client);
    const clients = useSelector((state) => state.client.clients);

    useEffect(() => {
        dispatch(getDatevConnections());
    }, [dispatch]);

    const user = useSelector((state) => state.authentication.user);

    const isSuperUser = useMemo(
        () => user && (user.userRole === UserRole.SUPER_USER || user.userRole === UserRole.TECHNICAL_USER),
        [user]
    );

    const stripeConnectionOf = (client) =>
        (client.connectedTo || []).find((connection) => connection.type === "STRIPE_CONNECT");

    const getStripeDashboardUrl = (isSuperUser, customerStripeEndpoint) => {
        const pathwayStripeEndpoint = process.env.REACT_APP_PATHWAY_STRIPE_CONNECTION_ENDPOINT;

        if (isSuperUser && pathwayStripeEndpoint) {
            return `https://dashboard.stripe.com/${pathwayStripeEndpoint}/connect/view-as/${customerStripeEndpoint}/balance/overview`;
        }

        return `https://dashboard.stripe.com/${customerStripeEndpoint}`;
    };

    return (
        <>
            <PathwayTable>
                <TopTableContent>
                    <WarningMessage marginless messages={clientReducer.datevConnectionsWarningMessage} />
                </TopTableContent>
                <TableHeader>
                    <PathwayTableHeader>Mandant</PathwayTableHeader>
                    <PathwayTableHeader>Verbindungen</PathwayTableHeader>
                    <PathwayTableHeader>DATEV</PathwayTableHeader>
                    <PathwayTableHeader />
                </TableHeader>

                {clients.map((client, index1) => (
                    <React.Fragment key={client.externalUUID}>
                        <TableRow>
                            <ClientCell>
                                <ClientName>{client.name}</ClientName>
                                {client.note && <ClientNote>{client.note}</ClientNote>}
                            </ClientCell>
                            <ButtonCell>
                                <PathwayButton
                                    color="tinted"
                                    dataCy={"add-connection-button-" + index1}
                                    onClick={() => props.createNewConnectedAuthDataForClient(client)}
                                >
                                    <i className="fa fa-plus" aria-hidden="true" />
                                    Verbindung hinzufügen
                                </PathwayButton>
                                {client.connectedTo.map((connection, index2) => (
                                    <PathwayConnectionChip
                                        key={connection.externalUUID}
                                        title={connection.title}
                                        logo={getIntegrationLogo(connection.type)}
                                        icon={getIntegrationIcon(connection.type)}
                                        status={connectionStatusSeverity(connection.status)}
                                        dataCy={
                                            stringUtils.formatUppercaseIdentifierToLowercase(connection.type) +
                                            "-connection-button-" +
                                            index1 +
                                            "-" +
                                            index2
                                        }
                                        onClick={() => props.editExistingConnectedAuthData(connection)}
                                    >
                                        {isAwaitingSetup(connection) && (
                                            <LoadingSpinner size="1.6rem" loading={true} primary={false} />
                                        )}
                                        {isStalledPaypalSetup(connection) && (
                                            <PathwayStatusPill severity="warning">
                                                Bitte erneut versuchen
                                            </PathwayStatusPill>
                                        )}
                                        {connection.status === AuthDataStatus.REVOKED && (
                                            <PathwayStatusPill severity="error">Deinstalliert</PathwayStatusPill>
                                        )}
                                    </PathwayConnectionChip>
                                ))}
                            </ButtonCell>
                            <DatevCell>
                                <DatevPair data-cy="datev-row">
                                    <DatevLabel>Berater</DatevLabel>
                                    <DatevValue>{client.consultantNumber || "—"}</DatevValue>
                                    <DatevSeparator aria-hidden="true">·</DatevSeparator>
                                    <DatevLabel>Mandat</DatevLabel>
                                    <DatevValue>{client.clientNumber || "—"}</DatevValue>
                                </DatevPair>
                                {(client.skr || client.accountNumberLength) && (
                                    <DatevPair>
                                        {client.skr && (
                                            <>
                                                <DatevLabel>SKR</DatevLabel>
                                                <DatevValue>{client.skr?.replace("_", " ")}</DatevValue>
                                            </>
                                        )}
                                        {client.skr && client.accountNumberLength && (
                                            <DatevSeparator aria-hidden="true">·</DatevSeparator>
                                        )}
                                        {client.accountNumberLength && (
                                            <>
                                                <DatevLabel>SKL</DatevLabel>
                                                <DatevValue>{client.accountNumberLength}-stellig</DatevValue>
                                            </>
                                        )}
                                    </DatevPair>
                                )}
                            </DatevCell>
                            <ActionsCell>
                                <PathwayRowActions>
                                    {(() => {
                                        const stripeConn = stripeConnectionOf(client);
                                        return (
                                            stripeConn && (
                                                <PathwayHint hint="Zum Stripe Connect Account">
                                                    <PathwayIconButton
                                                        type="button"
                                                        className="pw-icon-brand"
                                                        aria-label="Zum Stripe Connect Account"
                                                        data-cy={`stripe-connect-button-${index1}`}
                                                        onClick={() =>
                                                            window.open(
                                                                getStripeDashboardUrl(isSuperUser, stripeConn.endpoint),
                                                                "_blank",
                                                                "noopener,noreferrer"
                                                            )
                                                        }
                                                    >
                                                        <i
                                                            className="fa fa-arrow-up-right-from-square"
                                                            aria-hidden="true"
                                                        />
                                                    </PathwayIconButton>
                                                </PathwayHint>
                                            )
                                        );
                                    })()}
                                    <PathwayHint hint="Mandant bearbeiten">
                                        <PathwayIconButton
                                            type="button"
                                            className="pw-icon-brand"
                                            aria-label="Mandant bearbeiten"
                                            data-cy={"client-edit-button-" + index1}
                                            onClick={() => props.openEditClientModal(client)}
                                        >
                                            <i className="fa fa-pen" aria-hidden="true" />
                                        </PathwayIconButton>
                                    </PathwayHint>
                                    <PathwayHint hint="Mandant löschen">
                                        <PathwayIconButton
                                            type="button"
                                            className="pw-icon-danger"
                                            aria-label="Mandant löschen"
                                            data-cy={"client-delete-button-" + index1}
                                            onClick={() => props.openDeleteClientModal(client)}
                                        >
                                            <i className="fa fa-trash" aria-hidden="true" />
                                        </PathwayIconButton>
                                    </PathwayHint>
                                </PathwayRowActions>
                            </ActionsCell>
                        </TableRow>
                        <TableContentDividerLine />
                    </React.Fragment>
                ))}
            </PathwayTable>
        </>
    );
};

export default ClientsScreenClientsTabContent;

const DESKTOP_COLS = "28fr 26fr 26fr 20fr";
const DESKTOP_AREAS = '"client connections datev actions"';

const TableHeader = styled(PathwayTableHeaderRow)`
    grid-template-columns: ${DESKTOP_COLS};

    ${tablet} {
        grid-template-columns: 1fr auto;
        & > :nth-child(3),
        & > :nth-child(4) {
            display: none;
        }
    }

    ${mobile} {
        display: none;
    }
`;

const TableRow = styled(PathwayTableContentRow)`
    grid-template-columns: ${DESKTOP_COLS};
    grid-template-areas: ${DESKTOP_AREAS};

    ${tablet} {
        grid-template-columns: 180px 1fr auto;
        grid-template-rows: auto auto;
        grid-template-areas:
            "client connections actions"
            "datev  connections .      ";
        row-gap: 0;
        column-gap: var(--gap-l);
    }

    ${mobile} {
        grid-template-columns: 1fr auto;
        grid-template-rows: auto auto auto;
        grid-template-areas:
            "client  actions    "
            "connections connections"
            "datev   datev      ";
        row-gap: var(--gap-s);
        column-gap: var(--gap-m);
    }
`;

const ClientName = styled.div`
    font-size: var(--pw-text-md);
    font-weight: var(--pw-weight-semibold);
    color: var(--text-heading);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ClientNote = styled.div`
    margin-top: 3px;
    font-size: var(--pw-text-sm);
    font-weight: var(--pw-weight-light);
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DatevPair = styled.div`
    font-size: var(--pw-text-hint);
    color: var(--text-body);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DatevLabel = styled.span`
    font-weight: var(--pw-weight-light);
    color: var(--text-muted);
`;

const DatevValue = styled.span`
    margin-left: 4px;
    font-weight: var(--pw-weight-semibold);
    color: var(--text-heading);
`;

const DatevSeparator = styled.span`
    margin: 0 6px;
    color: var(--pw-neutral-450);
`;

const ClientCell = styled(PathwayTableCellContent)`
    grid-area: client;
`;

const ButtonCell = styled(PathwayTableCellContent)`
    grid-area: connections;
    row-gap: var(--gap-s);
    align-self: start;
    align-content: start;
`;

const DatevCell = styled(PathwayTableCellContent)`
    grid-area: datev;
    align-self: center;

    ${tablet} {
        align-self: end;
        padding-bottom: 4px;
    }

    ${mobile} {
        align-self: start;
    }
`;

const ActionsCell = styled(PathwayTableCellContent)`
    grid-area: actions;
    align-self: start;
`;

const TopTableContent = styled.div`
    display: grid;
    grid-template-columns: auto max-content;
    column-gap: var(--gap-l);
    align-items: end;
`;
